import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listEligibleAssignees,
  ELIGIBLE_ASSIGNEES_PAGE_LIMIT,
} from '../api/listEligibleAssigneesApiService';
import { assignContactRequest } from '../api/assignContactRequestApiService';
import { reassignContactRequest } from '../api/reassignContactRequestApiService';
import type {
  EligibleAssignee,
  ReassignContactRequestResponseData,
} from '../types/assignContactRequestTypes';

const SEARCH_DEBOUNCE_MS = 500;

const LABELS = {
  ERROR_REQUIRED: 'To pole jest wymagane',
  UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

/** Which step of the (re)assign flow the modal is showing. */
export type AssignStep = 'select' | 'confirm';

interface UseAssignContactRequestOptions {
  contactRequestId: string | undefined;
  /**
   * Fires after a successful (re)assign. `wasReassign` is true when the request
   * already had an assignee (so the dedicated reassign endpoint was used, and
   * `data.notification_sent` reflects the best-effort email outcome).
   */
  onSuccess?: (
    data: ReassignContactRequestResponseData | undefined,
    wasReassign: boolean
  ) => void;
  /**
   * Fires when the assign call fails with a 409 (another admin assigned the
   * request first). The modal stays open showing `submitError`; the caller
   * should use this to refresh the underlying contact request in the background.
   */
  onConflict?: () => void;
}

export interface UseAssignContactRequestReturn {
  isOpen: boolean;
  /**
   * Pass the request's current assignee (if any) to start the reassignment
   * flow — it's shown in the "Assignment Details" card while the picker starts
   * empty. Omit/pass null|undefined for a plain assign of an unassigned request.
   */
  open: (current?: EligibleAssignee | null) => void;
  close: () => void;
  /**
   * True when the modal was opened on an already-assigned request — drives the
   * two-step confirm flow, the notify toggle, and the reassign endpoint.
   */
  isReassign: boolean;
  /** Which step the modal shows: the picker (`select`) or the review (`confirm`). */
  step: AssignStep;
  /** Advance from the picker to the confirmation step (validates a pick was made). */
  goToConfirm: () => void;
  /** Return from the confirmation step to the picker. */
  back: () => void;
  /** Whether to email the newly assigned admin (reassign flow only). */
  notify: boolean;
  setNotify: (value: boolean) => void;
  assignees: EligibleAssignee[];
  /** The current assignee `open()` was called with, shown as the "before" state. */
  initialSelectedAssignee: EligibleAssignee | null;
  isLoadingAssignees: boolean;
  isLoadingMoreAssignees: boolean;
  hasMoreAssignees: boolean;
  loadMoreAssignees: () => void;
  assigneesError: string;
  assigneeSearch: string;
  setAssigneeSearch: (value: string) => void;
  selectedAssigneeId: string;
  setSelectedAssigneeId: (id: string) => void;
  selectedAssigneeError: string;
  isSubmitting: boolean;
  submitError: string;
  submit: () => Promise<void>;
  canSubmit: boolean;
}

/**
 * Drives the "Assign Contact Request" modal: paginates + debounce-searches
 * eligible assignees while the modal is open, validates a selection was
 * made, and calls the assign API.
 */
export const useAssignContactRequest = ({
  contactRequestId,
  onSuccess,
  onConflict,
}: UseAssignContactRequestOptions): UseAssignContactRequestReturn => {
  const [isOpen, setIsOpen] = useState(false);

  const [assignees, setAssignees] = useState<EligibleAssignee[]>([]);
  const [page, setPage] = useState(1);
  const [hasMoreAssignees, setHasMoreAssignees] = useState(false);
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false);
  const [isLoadingMoreAssignees, setIsLoadingMoreAssignees] = useState(false);
  const [assigneesError, setAssigneesError] = useState('');

  const [rawSearch, setRawSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedAssigneeId, setSelectedAssigneeIdState] = useState('');
  const [initialSelectedAssignee, setInitialSelectedAssignee] =
    useState<EligibleAssignee | null>(null);
  const [isReassign, setIsReassign] = useState(false);
  const [step, setStep] = useState<AssignStep>('select');
  const [notify, setNotify] = useState(false);
  const [selectedAssigneeError, setSelectedAssigneeError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = useCallback(() => {
    setSelectedAssigneeIdState('');
    setInitialSelectedAssignee(null);
    setIsReassign(false);
    setStep('select');
    setNotify(false);
    setSelectedAssigneeError('');
    setSubmitError('');
  }, []);

  const open = useCallback(
    (current?: EligibleAssignee | null) => {
      resetForm();
      setRawSearch('');
      setDebouncedSearch('');
      if (current) {
        // Reassignment: remember the current assignee for the "Assignment
        // Details" card, but leave the picker empty so the admin must
        // actively choose a *new* assignee ("New Assignee Details").
        setInitialSelectedAssignee(current);
        setIsReassign(true);
      }
      setIsOpen(true);
    },
    [resetForm]
  );

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  const goToConfirm = useCallback(() => {
    if (!selectedAssigneeId) {
      setSelectedAssigneeError(LABELS.ERROR_REQUIRED);
      return;
    }
    setSubmitError('');
    setStep('confirm');
  }, [selectedAssigneeId]);

  const back = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setSubmitError('');
    setStep('select');
  }, [isSubmitting]);

  const setAssigneeSearch = useCallback((value: string) => {
    setRawSearch(value);
  }, []);

  // Debounce search input while the modal is open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const id = setTimeout(() => {
      setDebouncedSearch(rawSearch.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [rawSearch, isOpen]);

  // Fetch page 1 whenever the modal opens or the debounced search changes.
  useEffect(() => {
    if (!isOpen || !contactRequestId) {
      return;
    }
    let mounted = true;
    const fetchFirstPage = async () => {
      setIsLoadingAssignees(true);
      setAssigneesError('');
      const result = await listEligibleAssignees({
        contactRequestId,
        page: 1,
        limit: ELIGIBLE_ASSIGNEES_PAGE_LIMIT,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (!mounted) {
        return;
      }
      if (result.success) {
        setAssignees(result.data ?? []);
        setHasMoreAssignees(result.hasNextPage ?? false);
        setPage(1);
      } else {
        setAssignees([]);
        setHasMoreAssignees(false);
        setAssigneesError(result.error ?? LABELS.UNEXPECTED_ERROR);
      }
      setIsLoadingAssignees(false);
    };
    void fetchFirstPage();
    return () => {
      mounted = false;
    };
  }, [isOpen, debouncedSearch, contactRequestId]);

  const loadMoreAssignees = useCallback(() => {
    if (
      isLoadingAssignees ||
      isLoadingMoreAssignees ||
      !hasMoreAssignees ||
      !contactRequestId
    ) {
      return;
    }
    const nextPage = page + 1;
    setIsLoadingMoreAssignees(true);
    void (async () => {
      const result = await listEligibleAssignees({
        contactRequestId,
        page: nextPage,
        limit: ELIGIBLE_ASSIGNEES_PAGE_LIMIT,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (result.success) {
        setAssignees((prev) => {
          const incoming = result.data ?? [];
          const dedup = incoming.filter(
            (a) => !prev.some((existing) => existing.id === a.id)
          );
          return [...prev, ...dedup];
        });
        setHasMoreAssignees(result.hasNextPage ?? false);
        setPage(nextPage);
      } else {
        setHasMoreAssignees(false);
        setAssigneesError(result.error ?? LABELS.UNEXPECTED_ERROR);
      }
      setIsLoadingMoreAssignees(false);
    })();
  }, [
    isLoadingAssignees,
    isLoadingMoreAssignees,
    hasMoreAssignees,
    page,
    debouncedSearch,
    contactRequestId,
  ]);

  const setSelectedAssigneeId = useCallback((id: string) => {
    setSelectedAssigneeIdState(id);
    setSelectedAssigneeError('');
  }, []);

  const canSubmit = !isSubmitting;

  const submit = useCallback(async () => {
    if (!contactRequestId || isSubmittingRef.current) {
      return;
    }

    if (!selectedAssigneeId) {
      setSelectedAssigneeError(LABELS.ERROR_REQUIRED);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const result = isReassign
        ? await reassignContactRequest({
            contactRequestId,
            assigneeId: selectedAssigneeId,
            notify,
          })
        : await assignContactRequest({
            contactRequestId,
            assigneeId: selectedAssigneeId,
          });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.(result.data, isReassign);
      } else {
        setSubmitError(result.error ?? LABELS.UNEXPECTED_ERROR);
        if (result.conflict) {
          onConflict?.();
        }
      }
    } catch {
      setSubmitError(LABELS.UNEXPECTED_ERROR);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    contactRequestId,
    selectedAssigneeId,
    isReassign,
    notify,
    onSuccess,
    onConflict,
    resetForm,
  ]);

  return {
    isOpen,
    open,
    close,
    isReassign,
    step,
    goToConfirm,
    back,
    notify,
    setNotify,
    assignees,
    initialSelectedAssignee,
    isLoadingAssignees,
    isLoadingMoreAssignees,
    hasMoreAssignees,
    loadMoreAssignees,
    assigneesError,
    assigneeSearch: rawSearch,
    setAssigneeSearch,
    selectedAssigneeId,
    setSelectedAssigneeId,
    selectedAssigneeError,
    isSubmitting,
    submitError,
    submit,
    canSubmit,
  };
};
